import { Badge, Box, Dialog, DialogContent, DialogTitle, IconButton, Link, Typography } from "@mui/material";
import { useState, useMemo } from "react";
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth0 } from '@auth0/auth0-react';
import { useAppSelector } from "@/store/hooks";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { NewUserTask, NewUserTasksResponse } from "@/types/notifications";
import DeviceService from "@/services/devices";
import localconfig from "@/config/local-config.json";


// Helper function to extract task_id from link_url
const getTaskIdFromUrl = (url?: string) => {
  if (!url) return null;
  const match = url.match(/\/qr-code-request\/([^/?]+)/);
  return match ? match[1] : null;
};

const UnreadNotifications = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { getAccessTokenSilently } = useAuth0();
    const { data: userData } = useAppSelector((state) => state.user);
    const userId = userData?.id;

    const [modalData, setModalData] = useState<NewUserTasksResponse | undefined>(undefined);

    const getDeviceService = async () => {
        const accessToken = await getAccessTokenSilently();
        const token = import.meta.env.DEV ? localconfig.accessToken : accessToken;
        return new DeviceService(undefined, token);
    };

    // Notification API functions using DeviceService
    const notificationService = useMemo(() => {
        return {
            getUserTasks: async (page: number = 1, isRead?: boolean): Promise<NewUserTasksResponse> => {
                if (!userId) {
                    throw new Error('User ID is required but not available');
                }
                const service = await getDeviceService();
                return await service.getUnreadNotifications({ page, userId, isRead });
            },

            markTasksAsRead: async (taskIds: string[]): Promise<void> => {
                if (!userId) {
                    throw new Error('User ID is required but not available');
                }
                const service = await getDeviceService();
                await service.markNotificationsAsRead(taskIds, userId);
            }
        };
    }, [userId]);

    const unreadTasksQuery = useQuery({
      queryKey: ['unread-tasks', userId],
      queryFn: () => notificationService.getUserTasks(1, false),
      enabled: !!userId, // Only run when userId is available
      // refetchInterval: 30000,
      // refetchIntervalInBackground: false,

      //remove below to start polling
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
    });

    // Query for all notifications (for modal display) - only fetched when modal opens
    const allTasksQuery = useQuery({
      queryKey: ['all-tasks', userId],
      queryFn: () => notificationService.getUserTasks(1, undefined), // undefined to get all tasks
      enabled: false, // Only fetch when explicitly triggered
    });

    // Mutation to mark tasks as read using new API
    const markAsReadMutation = useMutation({
      mutationFn: notificationService.markTasksAsRead,
      onSuccess: () => {
        unreadTasksQuery.refetch();
        allTasksQuery.refetch();
      },
    });

    const openModalNotification = async () => {
      // Fetch all tasks for modal display
      const unreadTasksData = await unreadTasksQuery.refetch();
      if (unreadTasksData.data) {
        setModalData(unreadTasksData.data);
        // Mark only unread notifications as read when opening modal
        const unreadTaskIds = unreadTasksData.data.results
          .filter((task: NewUserTask) => !task.is_read)
          .map((task: NewUserTask) => task.notification_id);

        if (unreadTaskIds.length > 0) {
          markAsReadMutation.mutate(unreadTaskIds);
        }
      }
    };

    const formatDate = (dateString: string) => {
      return new Intl.DateTimeFormat(i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }).format(new Date(dateString));
    };

    return (
      <>
        {unreadTasksQuery.data?.count && unreadTasksQuery.data.count > 0 ? (
          <Badge
            badgeContent={unreadTasksQuery.data.count}
            color="error"
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.75rem',
                height: 20,
                minWidth: 20,
                backgroundColor: '#004B8D',
                top: 6,
                right: 6,
                color: 'white',
              }
            }}
          >
            <IconButton
              className="navbar-icon-btn"
              onClick={openModalNotification}
              title={t("notifications", "Notifications")}
            >
              <NotificationsOutlinedIcon />
            </IconButton>
          </Badge>
        ) : (
          <IconButton
            className="navbar-icon-btn"
            onClick={() => navigate('/my-tasks')} // Navigate to MyTasks when no unread notifications
            title={t("notifications", "Notifications")}
          >
            <NotificationsOutlinedIcon />
          </IconButton>
        )}

        {/* Notification Modal */}
        <Dialog
          open={!!modalData}
          onClose={() => setModalData(undefined)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxHeight: "80vh",
              boxShadow: 4,
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              pt: 3,
              pb: 1,
              borderBottom: "1px solid #e0e0e0",
              bgcolor: "#fff",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#212121",
                fontSize: "1.125rem",
              }}
            >
              {t("notifications", "Notifications")}
            </Typography>

            <IconButton
              onClick={() => setModalData(undefined)}
              size="small"
              sx={{
                color: "#424242",
                "&:hover": { color: "#000" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

            {/* View All link */}
            <Box
              sx={{
                px: 3,
                py: 1.5,
                bgcolor: "#f9fafb",
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <Link
                href="/my-tasks"
                underline="hover"
                color="primary"
                sx={{
                  fontSize: "0.9rem",
                  fontWeight: 500,
                }}
                onClick={() => setModalData(undefined)}
              >
                {t("viewAllNotifications", "View all Notifications")}
              </Link>
            </Box>
            {/* Content */}
            <DialogContent sx={{ p: 0 }}>
            <Box sx={{ maxHeight: "50vh", overflowY: "auto", bgcolor: "#fff" }}>
              {modalData?.results.map((task, index) => {
                const taskId = getTaskIdFromUrl(task.link_url);
                const isClickable = task.link_url && taskId && task.status === "COMPLETED";

                return (
                  <Box
                    key={task.notification_id || index}
                    component={isClickable ? Link : "div"}
                    href={isClickable ? `/walkdowns/qr-codes/${taskId}` : undefined}
                    target={isClickable ? "_blank" : undefined}
                    rel={isClickable ? "noopener noreferrer" : undefined}
                    onClick={() => {
                      if (isClickable) {
                        setModalData(undefined);
                      }
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      px: 3,
                      py: 2,
                      borderBottom:
                        index < modalData.results.length - 1
                          ? "1px solid #f1f1f1"
                          : "none",
                      bgcolor: task.is_read ? "transparent" : "#f9fafc",
                      textDecoration: "none",
                      cursor: isClickable ? "pointer" : "default",
                      "&:hover": {
                        bgcolor: "#f3f4f6",
                      },
                    }}
                  >
                    {/* Status Dot */}
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor:
                          task.status === "FAILED"
                            ? "#e53935"
                            : task.status === "COMPLETED"
                            ? "#43a047"
                            : "#fb8c00",
                        mt: 0.6,
                        mr: 2,
                        flexShrink: 0,
                      }}
                    />

                    {/* Text */}
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 0.5,
                          color:
                            task.status === "FAILED" ? "#e53935" :
                            isClickable ? "primary.main" : "text.primary",
                          fontWeight: task.is_read ? 400 : 600,
                          lineHeight: 1.4,
                          textDecoration: isClickable ? "underline" : "none",
                        }}
                      >
                        {task.message_text}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          fontSize: "0.75rem",
                        }}
                      >
                        {formatDate(task.created_at)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}

              {/* Empty state */}
              {(!modalData?.results || modalData.results.length === 0) && (
                <Box sx={{ p: 4, textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    {t("noNotifications", "No notifications available")}
                  </Typography>
                </Box>
              )}
            </Box>
          </DialogContent>
        </Dialog>
      </>
    );
};

export default UnreadNotifications;



